import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DownloadIcon } from "../../assets/iconComponents/DownloadIcon";
import { DownloadWrapper, ReportDataCellWrapper } from "./Grid.styles";
import { ReportStatusIcon } from "./ReportStatusIcon";
import { RefreshRequisitionRows } from "./Services/GridService";
import { RequisitionRow } from "../../recoil/types";
import { CellRendererProps } from "./types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useReportDownload } from "../../utils/hooks/useReportDownload";
import { UpdatedBadge } from "../NewRequisition/NewReqOrderPlacedCell";
import { themePalette } from "../../theme";

const getValue = (
  value: string | boolean | Record<string, unknown>,
  valueFormatted: string | null | undefined,
) => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof valueFormatted === "string") {
    return valueFormatted;
  }
  return "";
};

const getRowUnreadAfterDownload = (
  carrierStatus: string,
  aneuploidyStatus: string,
  carrierRead: boolean,
  aneuploidyRead: boolean,
): boolean => {
  const validAneuploidy =
    aneuploidyStatus !== "Not Ordered" && aneuploidyStatus !== "Processing";
  const validCarrier =
    carrierStatus !== "Not Ordered" && carrierStatus !== "Processing";

  if (validCarrier && !carrierRead) {
    return true;
  }
  if (validAneuploidy && !aneuploidyRead) {
    return true;
  }
  return false;
};

const ReportDataCell: React.FC<CellRendererProps> = ({
  value = "",
  data,
  colDef,
  node,
  valueFormatted = "",
  tabType,
  context,
  onPrintError,
}) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [downloaded, setDownloaded] = useState(false);
  const { downloadReport } = useReportDownload();
  const { setRequisitions, searchText } = useRequisitionSetter();
  const { isPhysicalDesktop, deviceType } = useDeviceHook();
  const isCarrierValid =
    colDef?.field === "carrier" &&
    data.carrier !== "Not Ordered" &&
    data.carrier !== "Processing";
  // Aneuploidy is valid if its value is not "Not Ordered" or "Processing", or
  // if the RhD value is a standalone result.
  const isAneuploidyValid =
    colDef?.field === "aneuploidy" &&
    ((data.aneuploidy !== "Not Ordered" && data.aneuploidy !== "Processing") ||
      data.RhD === "Detected" ||
      data.RhD === "Not Detected" ||
      data.RhD === "See Report");
  const isCarrierRead = isCarrierValid && data.carrierRead;
  const isAneuploidyRead = isAneuploidyValid && data.aneuploidyRead;
  const isLims =
    colDef?.field === "carrier" ? data.carrierLims : data.aneuploidyLims;

  const cellValue = getValue(value, valueFormatted);
  const isCfcResultAneuploidyCell =
    colDef?.field === "aneuploidy" && data.aneuploidyReportType === "cfc";
  const cfcIcon =
    isCfcResultAneuploidyCell && cellValue !== "Test Not Performed" ? (
      <FontAwesomeIcon aria-hidden icon={faEyeSlash} />
    ) : undefined;
  let secondaryLabel: React.ReactNode;
  if (isCfcResultAneuploidyCell) {
    secondaryLabel = data.aneuploidySecondaryLabel;
  } else if (colDef?.field === "carrier") {
    secondaryLabel = data.carrierSecondaryLabel;
  }
  const isReportDownloadable = isCarrierValid || isAneuploidyValid;
  const reportUpdate = isReportDownloadable ? data.reportUpdate : null;
  const isUpdatePending = Boolean(reportUpdate?.is_pending);
  if (isUpdatePending) {
    secondaryLabel = "Update requested";
  } else if (reportUpdate) {
    secondaryLabel = (
      <UpdatedBadge
        updatedAt={reportUpdate.requested_at}
        updatedBy={reportUpdate.requested_by_name}
        label="Update requested on"
        color={themePalette.neutral500}
      />
    );
  }

  let reportId = data.aneuploidyReportID;
  if (isCarrierValid) {
    reportId = data.carrierReportID;
  } else if (isCfcResultAneuploidyCell) {
    reportId = data.cfcReportID || "";
  }
  const reportType = isCarrierValid ? "carrier" : data.aneuploidyReportType;
  const downloadIconColor =
    isCarrierRead || isAneuploidyRead || downloaded ? "lightgray" : undefined;
  const markReportAsRead = () => {
    node?.setData({
      ...data,
      [isCarrierValid ? "carrierRead" : "aneuploidyRead"]: true,
      unread: getRowUnreadAfterDownload(
        data.carrier,
        data.aneuploidy,
        isCarrierValid ? true : data.carrierRead,
        isAneuploidyValid ? true : data.aneuploidyRead,
      ),
    } as RequisitionRow);
  };

  const handleDownload = async () => {
    const downloadSucceeded = await downloadReport(
      reportId,
      reportType,
      "grid",
    );

    if (!downloadSucceeded) {
      onPrintError?.("Failed to download report");
      return;
    }

    markReportAsRead();
    setDownloaded(true);
    RefreshRequisitionRows(
      auth!.token,
      setRequisitions,
      setLoggedOut,
      searchText,
    );
  };

  const handleCellClick = () => {
    if (deviceType === "desktop") {
      localStorage?.setItem(
        `btoProviderCurrentPage${tabType}`,
        JSON.stringify(context?.currentPage ?? 0),
      );
    }
  };

  const handleDownloadClick = async (e: React.MouseEvent) => {
    if (deviceType === "desktop") {
      e.stopPropagation();
    }
    await handleDownload();
  };

  const downloadIcon = isLims ? (
    <DownloadIcon
      title="This report is unavailable to download - please call customer service."
      color="#FFB6B3"
    />
  ) : (
    <Tooltip title="Download Report" arrow placement="top">
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <DownloadIcon color={downloadIconColor} />
      </span>
    </Tooltip>
  );

  const downloadButton = isReportDownloadable ? (
    <DownloadWrapper
      isDesktop={isPhysicalDesktop}
      onClick={isLims ? undefined : handleDownloadClick}
    >
      {downloadIcon}
    </DownloadWrapper>
  ) : null;

  return (
    <ReportDataCellWrapper onClick={handleCellClick}>
      <ReportStatusIcon
        value={cellValue}
        icon={cfcIcon}
        secondaryLabel={secondaryLabel}
        isMuted={isUpdatePending}
      />
      {downloadButton}
    </ReportDataCellWrapper>
  );
};

export default ReportDataCell;
